import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import {
  PencilSquareIcon,
  TrashIcon,
  DocumentArrowDownIcon,
  DevicePhoneMobileIcon,
  ArrowDownTrayIcon
} from '@heroicons/react/24/outline';
import { utils as XLSXUtils, write as XLSXWrite } from 'xlsx';
import JsPDF from 'jspdf';
import 'jspdf-autotable';
import { toast } from 'react-hot-toast';
import { parseDate } from 'utils';
import { showAlert } from 'services/alerts.service';
import { makeRequest } from 'services/request.service';
import TableComponent from 'components/common/TableComponent';
import SelectComponent from 'components/common/SelectComponent';
import ButtonComponent from 'components/common/ButtonComponent';
import ActionCardComponent from 'components/common/ActionCardComponent';
import EditDeviceModal from 'components/modals/EditDeviceModal';
import FarmQRModal from 'components/modals/FarmQRModal';

const DevicesGroupsManagement = ({
  devices,
  devicesGroups,
  onRefreshDevices,
  onRefreshDevicesGroups,
  user
}) => {
  const { t } = useTranslation();
  const [state, setState] = useState({
    selectedDevice: null,
    selectedDeviceGroup: '',
    searchTermDevicesGroups: '',
    showEditModal: false,
    isQrModalOpen: false
  });

  const { selectedDevice, showEditModal, isQrModalOpen } = state;

  // Helper for auth headers
  const getAuthHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('secret')}`,
    'Content-Type': 'application/json'
  });

  // Create select options for device groups
  const devicesGroupsSelectOptions = devicesGroups.map(group => ({
    value: group.deviceGroupId.toString(),
    label: group.groupName || 'Unnamed Group'
  }));

  // Map devices with their group names for the top table
  const devicesInGroupsMapping = devices.map(device => {
    const group = devicesGroupsSelectOptions.find(
      g => String(g.value) === String(device.deviceGroupId)
    );
    return {
      deviceId: device.deviceId,
      type: device.type,
      managerDevice: device.managerDevice,
      identifier: device.identifier,
      groupName: group ? group.label : 'Unassigned',
      appVersion: device.appVersion,
      lastSync: device.lastSync
    };
  });

  // Edit device
  const handleEditDevice = async device => {
    try {
      const response = await makeRequest({
        method: 'PUT',
        url: `/management/devices/${device.deviceId}`,
        data: { managerDevice: device.managerDevice },
        headers: getAuthHeaders(),
        alertResult: true
      });
      if (response.status === 200) {
        setState(s => ({ ...s, showEditModal: false }));
        await onRefreshDevices();
      }
    } catch (error) {
      console.error('Error editing device:', error);
      setState(s => ({ ...s, showEditModal: false }));
    }
  };

  // Delete device in group (top table)
  const handleDeleteDeviceInGroup = async device => {
    try {
      const response = await makeRequest({
        method: 'DELETE',
        url: `/management/devices/${device.deviceId}`,
        headers: getAuthHeaders(),
        alertResult: true
      });

      if (response.status === 200) {
        await onRefreshDevices();
        await onRefreshDevicesGroups();
        showAlert({ message: t('devices.deleteSuccess'), icon: 'success' });
      }
    } catch (err) {
      console.error('Error deleting device:', err);
      showAlert({ message: t('devices.deleteFailed'), icon: 'error' });
    }
  };

  const handleExportExcel = () => {
    try {
      const dataToExport = devices.map(device => ({
        [t('devices.deviceIdentifier')]: device.identifier,
        [t('devices.deviceType')]: device.type,
        [t('devices.groupName')]: device.groupName,
        [t('devices.managerDevice')]: device.managerDevice,
        [t('devices.appVersion')]: device.appVersion,
        [t('devices.lastSync')]: device.lastSync
      }));

      const worksheet = XLSXUtils.json_to_sheet(dataToExport);
      const workbook = XLSXUtils.book_new();
      XLSXUtils.book_append_sheet(workbook, worksheet, 'Devices');

      const excelBuffer = XLSXWrite(workbook, {
        bookType: 'xlsx',
        type: 'array'
      });

      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `devices_${new Date().toISOString().split('T')[0]}.xlsx`;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      window.URL.revokeObjectURL(url);

      toast.success(t('export.excelSuccess'));
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      toast.error(t('export.excelFailed'));
    }
  };

  const handleExportPDF = () => {
    try {
      const doc = new JsPDF();

      doc.setFontSize(16);
      doc.text(t('devices.devicesReport'), 14, 15);
      doc.setFontSize(10);
      doc.text(`${t('export.generatedOn')}: ${new Date().toLocaleDateString()}`, 14, 25);

      const tableColumn = [
        t('devices.deviceIdentifier'),
        t('devices.deviceType'),
        t('devices.groupName'),
        t('devices.managerDevice'),
        t('devices.appVersion'),
        t('devices.lastSync')
      ];

      const tableRows = devices.map(device => [
        device.identifier,
        device.type,
        device.groupName,
        device.managerDevice,
        device.appVersion,
        device.lastSync
      ]);

      // @ts-ignore
      doc.autoTable({
        startY: 30,
        head: [tableColumn],
        body: tableRows,
        theme: 'grid',
        styles: {
          fontSize: 8,
          cellPadding: 2
        },
        headStyles: {
          fillColor: [67, 160, 71],
          textColor: 255
        }
      });

      doc.save(`devices_${new Date().toISOString().split('T')[0]}.pdf`);

      toast.success(t('export.pdfSuccess'));
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      toast.error(t('export.pdfFailed'));
    }
  };

  const handleChangeSelect = e => {
    setState(s => ({
      ...s,
      selectedDeviceGroup: e.target.value
    }));
  };

  const handleSearchChange = e => {
    setState(s => ({ ...s, searchTermDevicesGroups: e.target.value }));
  };

  const devicesGroupsTableData = {
    hasCheckbox: true,
    columns: [
      { key: 'type', label: t('devices.deviceType') },
      { key: 'identifier', label: t('devices.deviceIdentifier') },
      { key: 'groupName', label: t('devices.groupName') },
      {
        key: 'managerDevice',
        label: t('devices.managerDevice'),
        cell: value => (value ? t('common.yes') : t('common.no'))
      },
      { key: 'appVersion', label: t('devices.appVersion') },
      {
        key: 'lastSync',
        label: t('devices.lastSync'),
        cell: value => parseDate(value)
      },
      {
        key: 'actions',
        label: t('table.manage'),
        cell: (value, row) => (
          <div className="flex items-center gap-2">
            <button
              onClick={() => setState(s => ({ ...s, showEditModal: true, selectedDevice: row }))}
              title={t('common.edit')}
              className="p-1 rounded transition"
            >
              <PencilSquareIcon className="w-5 h-5 text-blue-500" />
            </button>
            <button
              onClick={() => handleDeleteDeviceInGroup(row)}
              title={t('common.delete')}
              className="p-1 rounded transition"
            >
              <TrashIcon className="w-5 h-5 text-red-500" />
            </button>
          </div>
        )
      }
    ],
    rows: [...devicesInGroupsMapping]
  };

  const filteredDevicesGroups = {
    ...devicesGroupsTableData,
    rows: devicesGroupsTableData.rows.filter(row => {
      const searchTerm = state.searchTermDevicesGroups.toLowerCase();

      const isMatchingText =
        row.type.toLowerCase().includes(searchTerm) ||
        row.identifier.toLowerCase().includes(searchTerm) ||
        row.groupName.toLowerCase().includes(searchTerm) ||
        row.appVersion.toLowerCase().includes(searchTerm);

      const isMatchingGroup =
        state.selectedDeviceGroup === '' ||
        row.groupName ===
          devicesGroupsSelectOptions.find(g => g.value === state.selectedDeviceGroup)?.label;

      return isMatchingText && isMatchingGroup;
    })
  };

  return (
    <>
      {/* Devices in Groups */}
      <ActionCardComponent>
        <input
          type="text"
          placeholder={t('devices.searchDeviceGroup')}
          value={state.searchTermDevicesGroups}
          onChange={handleSearchChange}
          data-align="left"
          className="py-2 px-3 border rounded-md shadow-sm"
        />
        <SelectComponent
          id="selectedDeviceGroup"
          data-align="left"
          name="selectedDeviceGroup"
          value={state.selectedDeviceGroup || ''}
          onChange={handleChangeSelect}
        >
          <option key="" value="">
            {t('devices.allDevicesInGroups')}
          </option>
          {devicesGroupsSelectOptions.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </SelectComponent>

        <a
          href="https://expo.dev/artifacts/eas/aa5KeTp57sDXH2Cd4xdHNo.apk"
          download
          className="py-2 rounded-lg shadow-md flex items-center gap-2 px-4 rounded-md min-w-max bg-greenPrimary text-white hover:bg-green-700 transition"
        >
          <ArrowDownTrayIcon className="w-5 h-5" />
          <span className="font-medium">{t('devices.downloadApk')}</span>
        </a>
        <ButtonComponent onClick={() => setState(s => ({ ...s, isQrModalOpen: true }))}>
          <DevicePhoneMobileIcon className="w-5 h-5" />
          <span className="font-medium">{t('devices.registerDevice')}</span>
        </ButtonComponent>
        <ButtonComponent onClick={handleExportExcel}>
          <DocumentArrowDownIcon className="w-5 h-5" />
          <span className="font-medium">{t('export.excel')}</span>
        </ButtonComponent>
        <ButtonComponent onClick={handleExportPDF}>
          <DocumentArrowDownIcon className="w-5 h-5" />
          <span className="font-medium">{t('export.pdf')}</span>
        </ButtonComponent>
      </ActionCardComponent>

      <TableComponent
        tableData={filteredDevicesGroups}
        defaultSortColumn={filteredDevicesGroups.columns[0]?.key}
      />

      {/* Modals */}
      <FarmQRModal
        isOpen={isQrModalOpen}
        onClose={() => setState(s => ({ ...s, isQrModalOpen: false }))}
        farmId={user.farmId}
        farmName={user.farmName}
      />

      {showEditModal && (
        <EditDeviceModal
          device={selectedDevice}
          onClose={() => setState(s => ({ ...s, showEditModal: false, selectedDevice: {} }))}
          onSave={handleEditDevice}
        />
      )}
    </>
  );
};

DevicesGroupsManagement.propTypes = {
  devices: PropTypes.arrayOf(PropTypes.object),
  devicesGroups: PropTypes.arrayOf(PropTypes.object),
  onRefreshDevices: PropTypes.func,
  onRefreshDevicesGroups: PropTypes.func,
  user: PropTypes.shape({ farmId: PropTypes.string, farmName: PropTypes.string })
};

export default DevicesGroupsManagement;
